import React, { useState, useEffect } from "react";
import clsx from "clsx";

import { Header, TableBody, TableHeader, Footer } from "./modules";

import { getUUID } from "./utils";
import { SORT_ORDER } from "./constants";

import "./assets/styles/style.css";

const DataTable = ({
  title,
  isLoading,
  wrapperClassName = "",
  columns,
  rows = [],
  paginate = true,
  pageSizeOptions = [10],
  searchableFields = [],
  inlineUpdate = false,
  onSelect,
  onAdd,
  onEdit,
  onSync,
  onDelete,
  ActionComponent,
}) => {
  const [rowsData, setRowsData] = useState([]);
  const [selectedRows, setSelectedRow] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [rowToEdit, setRowToEdit] = useState(null);
  const [isSyncInProgress, setIsSyncInProgress] = useState(false);
  const [paginationState, setPaginationState] = useState({
    enabled: paginate,
    pageSize: pageSizeOptions[0] || 10,
    pageNo: 1,
    totalPages: 0,
  });
  const [filterState, setFilterState] = useState({
    sortBy: null,
    search: "",
  });

  const handlePaginationChange = (key, value) => {
    setPaginationState((prevState) => ({
      ...prevState,
      pageNo: key === "pageSize" ? 1 : prevState.pageNo,
      [key]: value,
    }));
  };

  const handleFilterChange = (key, value) => {
    handlePaginationChange("pageNo", 1);
    setFilterState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
    handleRowSelect(null, null, false);
  };

  const handleRowSelect = (row__id, isSelected, all) => {
    let updatedItems = [...selectedRows];
    const allFilteredRows = getRows(false);
    let selectedRowsData;

    if (typeof all !== "undefined") {
      if (all === true) {
        selectedRowsData = [...allFilteredRows.data];
      } else selectedRowsData = [];

      updatedItems = selectedRowsData.map(({ __id }) => __id);
    } else {
      if (isSelected) updatedItems = [...updatedItems, row__id];
      else updatedItems = updatedItems.filter((i) => i !== row__id);

      selectedRowsData = allFilteredRows.data.filter((row) =>
        updatedItems.includes(row.__id)
      );
    }

    setSelectedRow(updatedItems);
    onSelect && onSelect(selectedRowsData);
  };

  const handleRowEditSave = async (row) => {
    if (inlineUpdate) {
      let _rowsData = [...rowsData];
      const rowIndex = _rowsData.findIndex((_row) => _row.__id === rowToEdit);
      _rowsData[rowIndex] = row;

      setRowsData([..._rowsData]);
      setRowToEdit(false);
      onEdit(row);
    } else {
      await onEdit(row);
      setRowToEdit(false);
    }
  };

  const handleSync = async () => {
    setIsSyncInProgress(true);
    await onSync();
    setIsSyncInProgress(false);
  }

  const handleRowAddSave = async (data) => {
    if (inlineUpdate) {
      let _rowsData = [data, ...rowsData];
      setRowsData([..._rowsData]);
      setShowAddForm(false);
      onEdit(data);
    } else {
      await onAdd(data);
      setShowAddForm(false);
    }
  };

  const _sortData = (data, sort) => {
    if (!sort || sort.index === -1) return data;

    let sortedData = [...data].sort((a, b) => {
      let comparison = 0,
        FieldA = a[sort.field],
        FieldB = b[sort.field];

      if (!FieldA && !FieldB) return comparison;

      if (columns[sort.index] && columns[sort.index].sortHandler) {
        return columns[sort.index].sortHandler(FieldA, FieldB);
      }

      FieldA = FieldA && FieldA.toString().trim().toLowerCase();
      FieldB = FieldB && FieldB.toString().trim().toLowerCase();

      if (FieldA && !FieldB) comparison = 1;
      else if (!FieldA && FieldB) comparison = -1;
      else if (FieldA > FieldB) comparison = 1;
      else if (FieldA < FieldB) comparison = -1;

      return comparison;
    });

    sortedData =
      sort.order === SORT_ORDER.DESC ? sortedData.reverse() : sortedData;

    return sortedData;
  };

  const _filterData = (data, search) => {
    let updatedData = [...data];

    updatedData = updatedData.filter((item) => {
      const result = searchableFields.map((key) => {
        return (
          (item[key] && item[key].toString().toLowerCase().includes(search)) ||
          false
        );
      });
      return result.includes(true);
    });

    return updatedData;
  };

  const hasActionColumn = () => {
    if (ActionComponent || onEdit || onDelete || showAddForm) {
      return true;
    }
    return false;
  };

  const isAllRowsSelected = () => {
    const { data } = getRows(false);
    return data.length === selectedRows.length && data.length !== 0;
  };

  const getRows = (enablePagination = paginationState.enabled) => {
    const { pageSize, pageNo } = paginationState;
    const { search, sortBy } = filterState;
    const offset = pageSize * pageNo - pageSize;

    let data = [...rowsData];
    let count = data.length;

    if (search && search.trim() && searchableFields.length) {
      data = _filterData(data, search.toString().toLowerCase());
      count = data.length;
    }

    if (sortBy) {
      data = _sortData(data, sortBy);
      count = data.length;
    }

    if (enablePagination) {
      count = data.length;
      data = data.slice(offset, offset + pageSize);
    }

    return { data, count };
  };

  const filteredRows = getRows();

  useEffect(() => {
    const updatedData = rows.map((row, i) => ({ ...row, __id: getUUID(i) }));
    setRowsData(updatedData);
    setSelectedRow([]);
  }, [rows]);

  return (
    <div className={clsx("datatable", wrapperClassName)}>
      {title && <div className="datatable_title">{title}</div>}

      <Header
        paginate={paginate}
        pageSizeOptions={pageSizeOptions}
        searchableFields={searchableFields}
        paginationState={paginationState}
        filterState={filterState}
        showAddForm={showAddForm}
        noAction={!!rowToEdit || isLoading}
        updatePagination={handlePaginationChange}
        onFilterChange={handleFilterChange}
        onAdd={onAdd && setShowAddForm}
        onSync={onSync && handleSync}
        isSyncInProgress={isSyncInProgress}
      />

      <div className="datatable_grid">
        <table cellPadding={0} cellSpacing={0}>
          <TableHeader
            columns={columns}
            sortBy={filterState.sortBy}
            onSort={(value) => handleFilterChange("sortBy", value)}
            onSelect={onSelect && handleRowSelect}
            allowActions={hasActionColumn()}
            noAction={!!rowToEdit || showAddForm}
            isAllSelected={isAllRowsSelected()}
          />
          <TableBody
            isLoading={isLoading}
            Action={ActionComponent}
            columns={columns}
            noAction={!!rowToEdit || showAddForm}
            rowsToDisplay={filteredRows.data}
            selectedRows={selectedRows}
            rowBeingEdited={rowToEdit}
            onDelete={onDelete}
            onSelect={onSelect && handleRowSelect}
            onEdit={onEdit && setRowToEdit}
            onEditComplete={handleRowEditSave}
            onEditCancel={() => setRowToEdit(null)}
            onSaveComplete={showAddForm && handleRowAddSave}
            onSaveCancel={() => setShowAddForm(false)}
            hasActions={hasActionColumn()}
          />
        </table>
      </div>

      {paginate && rowsData.length > 0 && (
        <Footer
          filteredRows={filteredRows}
          paginationState={paginationState}
          onPaginationChange={handlePaginationChange}
          noAction={!!rowToEdit || showAddForm}
        />
      )}
    </div>
  );
};

export default DataTable;